import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Animated, Easing, ActivityIndicator, View } from 'react-native'
import { useIsFocused, useNavigation, useRoute } from '@react-navigation/native'
import ReactNativeBlobUtil from 'react-native-blob-util'
import { capitalize, isEqual, partition } from 'lodash'
import moment from 'moment'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { StyleService, useStyleSheet } from '@src/style/service'
import { Button, Text } from '@components/base'
import { Feature, ScrollableAvoidKeyboard, useFeatureFlag } from '@components'
import { Bugsnag, ErrorMessages, Logger, LoggerScreens, Messages } from '@config'
import { DATE_FORMAT } from '@config/momentFormat'
import { useApplicationActionSheet } from '@src/hooks/useApplicationActionSheet'
import { AppRouteProp } from '@navigation/types'
import { IngredientItem } from '@screens/Ingredients'
import {
  ingredientsAreEqual,
  shouldUpdateIngredientData,
  updateIngredientsData,
} from '@screens/Ingredients/utils'
import { useDescriptionSuggestions } from '@screens/Ingredients/utils/hooks'
import { BaseIngredient } from '@screens/Ingredients/types'
import {
  DatePicker,
  MealDescription,
  PhotoPicker,
  PillButton,
  SuggestionsPicker,
} from '@screens/Meals'
import { useUpdateDraftMeal } from '@screens/Meals/hooks'
import { useMealPhotoUpload } from '@screens/Meals/hooks/useMealPhotoUpload'
import { getDefaultMealKind, transformToMealPayload } from '@screens/Meals/utils'
import { mealTypesSelector } from '@selectors/app'
import { mealDraftSelector } from '@selectors/meals'
import { useGoBack } from '@utils/navigation'
import { useSnack } from '@utils/navigatorContext'
import { ImageDescription, Meal, MealKind } from '@src/types'
import { loadingEffectsSelector } from '@src/selectors/loading'
import { generateReactNativeFile, ReactNativeFile } from '@src/utils/image'
import { useLazyScreenPreload } from '@src/navigation/utils'
import { hoverTime } from '@src/screens/Home/hooks/useChartInteractions'
import { WithWalkthrough } from '@src/components/WithWalkthrough'
import { FavoriteToggle } from '@src/components/FavoriteToggle'
import { MfpPromotionCard } from '../components/MfpPromotionCard'

const MAX_MEAL_OPTIONS_HEIGHT = 208
const MEAL_OPTIONS_PHOTO_HEIGHT = 176
const MEAL_OPTIONS_MARGIN_TOP = 20
const DATE_TOOLTIP_TIMEOUT = 2000

export const MealEditor = () => {
  const insets = useSafeAreaInsets()
  const styles = useStyleSheet(themedStyle)

  const dispatch = useDispatch()
  const navigation = useNavigation()
  const goBack = useGoBack()
  const showSnack = useSnack()
  const updateDraftMeal = useUpdateDraftMeal()
  const { showActionSheet } = useApplicationActionSheet()

  const { params } = useRoute<AppRouteProp<'AddMeal'>>()
  const isFocused = useIsFocused()

  const isSubmitting = useRef(false)
  const isMounted = useRef(true)
  const loadedPhotoDescriptionUri = useRef<string>(undefined)
  const uploadingPhotoUri = useRef<string | undefined>(undefined)
  const ignoredSuggestions = useRef<BaseIngredient[]>([])
  const optionsHeight = useRef(new Animated.Value(MAX_MEAL_OPTIONS_HEIGHT)).current
  const optionsMarginTop = useRef(new Animated.Value(MEAL_OPTIONS_MARGIN_TOP)).current
  const [fileExists, setFileExists] = useState(false)
  const [editingDescription, setEditingDescription] = useState(false)
  const [isDateBasedOnFile, setIsDateBasedOnFile] = useState(false)
  const [dateTooltipVisible, setDateTooltipVisible] = useState(false)
  const [settledUploadUri, setSettledUploadUri] = useState<string | undefined>(undefined)
  const dateTooltipTimeoutRef = useRef<NodeJS.Timeout>(null)

  const populateMealTimeFromPhoto = useFeatureFlag(Feature.PopulateMealTimeFromPhoto)
  const showMealTimeTooltip = useFeatureFlag(Feature.ShowMealTimeTooltip)
  const directS3UploadEnabled = useFeatureFlag(Feature.DirectS3MealUpload)
  const { uploadMealPhoto } = useMealPhotoUpload()

  const mealTypes = useSelector(mealTypesSelector)
  const draftMeal = useSelector(mealDraftSelector)
  const isGeneratingDescriptionFromImage =
    useSelector(loadingEffectsSelector)['meals/getMealImageDescription'] || false

  const {
    description = '',
    favorite,
    file,
    photoSignedId,
    occurredAt,
    ingredients = [],
    kind,
  } = draftMeal
  const mealOptionsHeight = file?.uri
    ? MAX_MEAL_OPTIONS_HEIGHT + MEAL_OPTIONS_PHOTO_HEIGHT
    : MAX_MEAL_OPTIONS_HEIGHT

  // Surface the "Processing photo" state across the whole pipeline, not just the
  // AI description call: when DirectS3MealUpload is on, a freshly picked photo
  // first uploads to S3 (hash + createDirectUpload + PUT) before the description
  // query can run, and that window was previously silent. Stays true from the
  // moment a local photo is picked until its description resolves (success or
  // failure both move loadedPhotoDescriptionUri to the file's uri).
  const isProcessingPhoto =
    isGeneratingDescriptionFromImage ||
    (!!file?.uri && !file.uri.startsWith('http') && file.uri !== loadedPhotoDescriptionUri.current)

  const descriptionSuggestions = useDescriptionSuggestions(description)

  const date = moment(occurredAt || hoverTime).toDate()

  const setDate = useCallback(
    (newDate: Date) => {
      updateDraftMeal({ occurredAt: moment(newDate).local().format() })
    },
    [updateDraftMeal],
  )

  const showDateTooltip = useCallback(() => {
    setDateTooltipVisible(true)
    if (dateTooltipTimeoutRef.current) {
      clearTimeout(dateTooltipTimeoutRef.current)
    }
    dateTooltipTimeoutRef.current = setTimeout(() => {
      setDateTooltipVisible(false)
    }, DATE_TOOLTIP_TIMEOUT)
  }, [])

  const hideDateTooltip = useCallback(() => {
    setDateTooltipVisible(false)
    if (dateTooltipTimeoutRef.current) {
      clearTimeout(dateTooltipTimeoutRef.current)
    }
  }, [])

  const manualSetDate = useCallback(
    (newDate: Date) => {
      setIsDateBasedOnFile(false)
      hideDateTooltip()
      setDate(newDate)
    },
    [hideDateTooltip, setDate],
  )

  const onFileSelected = useCallback(
    (file: ReactNativeFile | null) => {
      const params: { file: ReactNativeFile | null; occurredAt?: string } = { file }

      if (file?.timestamp && populateMealTimeFromPhoto) {
        params.occurredAt = moment(file.timestamp).local().format()
        setIsDateBasedOnFile(true)
        if (showMealTimeTooltip) {
          showDateTooltip()
        }
      }

      if (!file && isDateBasedOnFile) {
        setIsDateBasedOnFile(false)
        params.occurredAt = moment(hoverTime).local().format()
      }

      if (!file) {
        uploadingPhotoUri.current = undefined
        updateDraftMeal({ ...params, photoSignedId: null })
      } else {
        const isNewLocalPhoto = !!file.uri && !file.uri.startsWith('http')

        // Always drop a stale signed id when a new local photo is picked, regardless of
        // the feature flag — otherwise transformToMealPayload could submit a signed id
        // that belongs to a previously selected photo.
        updateDraftMeal(isNewLocalPhoto ? { ...params, photoSignedId: null } : params)

        if (directS3UploadEnabled && isNewLocalPhoto) {
          uploadingPhotoUri.current = file.uri
          setSettledUploadUri(undefined)
          uploadMealPhoto(generateReactNativeFile(file)).then((signedId) => {
            // Drop the result if the screen unmounted or a newer photo was selected
            // (incl. after submit, which clears uploadingPhotoUri) so we never write
            // into a cleared/replaced draft.
            if (!isMounted.current || uploadingPhotoUri.current !== file.uri) {
              return
            }
            setSettledUploadUri(file.uri)
            if (signedId) {
              updateDraftMeal({ photoSignedId: signedId })
            }
          })
        }
      }
    },
    [
      directS3UploadEnabled,
      isDateBasedOnFile,
      populateMealTimeFromPhoto,
      setSettledUploadUri,
      showDateTooltip,
      showMealTimeTooltip,
      updateDraftMeal,
      uploadMealPhoto,
    ],
  )

  // Track mount state so a late S3 upload result doesn't update an unmounted screen
  useEffect(
    () => () => {
      isMounted.current = false
    },
    [],
  )

  // set initial meal time
  useEffect(() => {
    if (!hoverTime) {
      return
    }

    setDate(moment(hoverTime).toDate())

    if (showMealTimeTooltip) {
      setIsDateBasedOnFile(false)
      showDateTooltip()
    }
  }, [setDate, showDateTooltip, showMealTimeTooltip])

  useEffect(() => {
    return () => {
      if (dateTooltipTimeoutRef.current) {
        clearTimeout(dateTooltipTimeoutRef.current)
      }
    }
  }, [])

  // Automatically update ingredients list based on the description text
  useEffect(() => {
    // Only the focused editor may sync suggestions into the shared draft. Stacked
    // navigation screens stay mounted, so a second MealEditor instance (e.g. AddMeal
    // pushed again over an existing one) has its own suggestions state and ignored
    // list — two live editors reconciling the same draft dispatch opposing updates
    // in an endless loop ("Maximum update depth exceeded").
    if (!isFocused) {
      return
    }

    // Ignore the suggestions for existing meals (editing or adding from favorites/recents)
    if (draftMeal.createdAt || isSubmitting.current) {
      return
    }

    const [manuallyAdded, previousSuggestions] = partition(
      ingredients as BaseIngredient[],
      (ingredient) => !ingredient.isFromSuggestions,
    )
    // Always keep manually added ingredients in the list
    const ignoredIngredients = [...manuallyAdded, ...ignoredSuggestions.current]
    const filteredSuggestions = descriptionSuggestions
      .filter((suggestion) => {
        return !ignoredIngredients.some((ingredient) => ingredientsAreEqual(ingredient, suggestion))
      })
      .map((item) => ({ ...item, isFromSuggestions: true }))

    if (isEqual(filteredSuggestions, previousSuggestions)) {
      return
    }

    updateDraftMeal({ ingredients: [...manuallyAdded, ...filteredSuggestions] })
  }, [descriptionSuggestions, draftMeal.createdAt, ingredients, isFocused, updateDraftMeal])

  // Get full ingredients data
  useEffect(() => {
    const getFullIngredientsData = async () => {
      const updatedIngredients = await updateIngredientsData(ingredients)

      updateDraftMeal({ ingredients: updatedIngredients })
    }

    if (ingredients.some(shouldUpdateIngredientData)) {
      getFullIngredientsData()
    }
  }, [ingredients, updateDraftMeal])

  // Checks if the meal photo file still exists.
  // We do this to avoid trying to upload a file that's no longer available.
  useEffect(() => {
    const checkFileExists = async () => {
      if (!file?.uri) {
        setFileExists(false)
        return
      }

      if (file.uri.startsWith('http')) {
        return true
      }

      try {
        const path = file.uri.replace('file://', '')
        const fileExists = await ReactNativeBlobUtil.fs.exists(path)
        setFileExists(fileExists)

        if (!fileExists) {
          updateDraftMeal({ file: undefined })
        }
      } catch {
        setFileExists(false)
      }
    }

    checkFileExists()
  }, [file?.uri, updateDraftMeal])

  // Get auto-generated description from the selected meal photo
  useEffect(() => {
    const getDescriptionFromImage = (image: ReactNativeFile) => {
      const payload =
        directS3UploadEnabled && photoSignedId ? { imageSignedId: photoSignedId } : { image }

      dispatch({
        type: 'meals/getMealImageDescription',
        payload,
        success: (data: ImageDescription) => {
          loadedPhotoDescriptionUri.current = image.uri
          // Append description from image to existing description (if any)
          const trimmedCurrentDescription = description.trimEnd() || ''
          updateDraftMeal({
            description: trimmedCurrentDescription
              ? `${trimmedCurrentDescription}\n${data.description}`
              : data.description,
          })
        },
        failure: (error: Error) => {
          // Clear the processing state even on failure so the button doesn't get
          // stuck on "Processing photo" (we won't retry this uri automatically).
          loadedPhotoDescriptionUri.current = image.uri
          console.error(error)
        },
      })
    }

    const waitingForUpload =
      directS3UploadEnabled &&
      !!file &&
      !file.uri.startsWith('http') &&
      !photoSignedId &&
      settledUploadUri !== file.uri
    if (fileExists && file && !waitingForUpload && file.uri !== loadedPhotoDescriptionUri.current) {
      getDescriptionFromImage(generateReactNativeFile(file))
    }
  }, [
    description,
    directS3UploadEnabled,
    dispatch,
    file,
    fileExists,
    photoSignedId,
    settledUploadUri,
    updateDraftMeal,
  ])

  // Animate hide of meal options when user is focusing on description input
  useEffect(() => {
    Animated.timing(optionsHeight, {
      toValue: editingDescription ? 0 : mealOptionsHeight,
      duration: 200,
      easing: Easing.linear,
      useNativeDriver: false,
    }).start()
    Animated.timing(optionsMarginTop, {
      toValue: editingDescription ? 0 : MEAL_OPTIONS_MARGIN_TOP,
      duration: 200,
      easing: Easing.linear,
      useNativeDriver: false,
    }).start()
  }, [editingDescription, optionsHeight, optionsMarginTop, mealOptionsHeight])

  const onSavePress = () => {
    if (isSubmitting.current) {
      return
    }

    isSubmitting.current = true
    // Invalidate any in-flight photo upload so its late result can't repopulate
    // the draft that meals/submit is about to clear.
    uploadingPhotoUri.current = undefined

    try {
      const payload = transformToMealPayload(draftMeal)

      Logger.sendInfo(LoggerScreens.MealEditor, Messages.MealsSubmitAttempt, { payload })

      dispatch({
        type: 'meals/submit',
        payload,
        failure: (error: any) => {
          Logger.sendError(LoggerScreens.MealEditor, ErrorMessages.MealNotSubmitted, { payload })
          if (error?.message) {
            showSnack(error.message, null, 'error')
          }
        },
        success: (savedMeal: Meal) => {
          const mealDate = moment(savedMeal.occurredAt).format(DATE_FORMAT)

          dispatch({
            type: 'app/changeDateRange',
            payload: {
              startDate: mealDate,
              endDate: mealDate,
            },
          })
          Logger.sendInfo(LoggerScreens.MealEditor, Messages.MealsSubmitSuccess, { payload })
        },
      })

      if (params?.shouldGoBack) {
        goBack()
      } else {
        navigation.navigate('Dashboard')
      }
    } catch (error: any) {
      Bugsnag.notify(error)
    } finally {
      isSubmitting.current = false
    }
  }

  const deleteIngredient = useCallback(
    (ingredient: BaseIngredient) => {
      if (!ignoredSuggestions.current.some((item) => ingredientsAreEqual(item, ingredient))) {
        ignoredSuggestions.current.push(ingredient)
      }

      dispatch({
        type: 'meals/removeDraftIngredient',
        payload: ingredient,
      })
    },
    [dispatch],
  )

  const onEditIngredientPress = useCallback(
    (ingredient: BaseIngredient) => {
      navigation.navigate('IngredientPortion', { ingredient, isUpdatingIngredient: true })
    },
    [navigation],
  )

  const onAddIngredientsManually = () => {
    navigation.navigate('IngredientSearch', { mealType: kind })
  }

  useLazyScreenPreload('BarcodeIngredientSearch')

  const tooltipLabel = `${isDateBasedOnFile ? 'Photo' : 'Chart'} timestamp, tap to edit`

  return (
    <View style={[styles.container, { paddingBottom: insets.bottom }]}>
      <ScrollableAvoidKeyboard bounces={false} showsVerticalScrollIndicator={false}>
        <Animated.View
          style={[styles.mealOptions, { maxHeight: optionsHeight, marginTop: optionsMarginTop }]}
        >
          <PillButton
            icon="fork-knife"
            text={capitalize(params?.mealKind || kind || getDefaultMealKind(moment()))}
            testID="AddMealComponent/Type"
            accessibilityLabel="Category"
            onPress={() =>
              showActionSheet({
                title: 'Category',
                options: mealTypes,
                callback: (mealType) => {
                  updateDraftMeal({ kind: mealType.toLowerCase() as MealKind })
                },
              })
            }
          />
          <WithWalkthrough
            isVisible={dateTooltipVisible}
            placement="top"
            allowChildInteraction
            disableShadow
            contentStyle={styles.tooltipContentStyle}
            backgroundColor="transparent"
            onClose={hideDateTooltip}
            showChildInTooltip={false}
            content={
              <Text type="small" style={styles.tooltipText}>
                {tooltipLabel}
              </Text>
            }
            accessibilityLabel={tooltipLabel}
          >
            <DatePicker testID="AddMealComponent/Date" date={date} setDate={manualSetDate} />
          </WithWalkthrough>
          <PillButton
            icon="barcode"
            text="Scan Barcode"
            testID="AddMealComponent/Barcode"
            accessibilityLabel="Scan Barcode"
            onPress={() => navigation.navigate('BarcodeIngredientSearch')}
          />
          <PhotoPicker
            testID="AddMealComponent/Photo"
            file={file}
            loading={isProcessingPhoto}
            onFileSelected={onFileSelected}
          />
        </Animated.View>
        <MealDescription
          editingDescription={editingDescription}
          setEditingDescription={setEditingDescription}
        />
        {ingredients.length > 0 && (
          <>
            <Text type="regular" bold style={styles.ingredientsLabel}>
              Ingredients
            </Text>
            {(ingredients as BaseIngredient[]).map((item) => (
              <IngredientItem
                key={item.description}
                item={item}
                onItemSelect={onEditIngredientPress}
                onRemovePress={deleteIngredient}
              />
            ))}
          </>
        )}
        <SuggestionsPicker
          suggestions={descriptionSuggestions}
          ingredients={ingredients as BaseIngredient[]}
        />
        <Button
          type="outline"
          size="small"
          accessibilityLabel="Add Ingredients Manually"
          style={styles.addIngredients}
          onPress={onAddIngredientsManually}
        >
          Add ingredients manually
        </Button>
        <View style={styles.favoriteSection}>
          <Text type="regular">Add to favorites</Text>
          <FavoriteToggle
            isFavorite={favorite}
            onPress={() => updateDraftMeal({ favorite: !favorite })}
          />
        </View>
        <MfpPromotionCard />
      </ScrollableAvoidKeyboard>
      <Button
        type="primary"
        size="block"
        style={styles.saveButton}
        onPress={onSavePress}
        accessibilityLabel="Save meal"
        disabled={isProcessingPhoto}
        accessoryLeft={
          isProcessingPhoto ? (
            <ActivityIndicator key="activity-indicator" style={styles.activityIndicator} />
          ) : undefined
        }
      >
        {isProcessingPhoto ? 'Processing photo' : 'Save Meal'}
      </Button>
    </View>
  )
}

const themedStyle = StyleService.create({
  container: {
    flex: 1,
    backgroundColor: 'theme.background',
  },
  mealOptions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginHorizontal: 16,
    marginBottom: 16,
    overflow: 'hidden',
    rowGap: 16,
    columnGap: 8,
  },
  ingredientsLabel: {
    margin: 16,
  },
  addIngredients: {
    marginTop: 24,
    marginHorizontal: 16,
    alignSelf: 'stretch',
  },
  favoriteSection: {
    marginVertical: 32,
    marginHorizontal: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  saveButton: {
    margin: 16,
  },
  tooltipText: {
    color: 'theme.tooltip.textPrimary',
    textAlign: 'left',
  },
  tooltipContentStyle: {
    backgroundColor: 'theme.tooltip.background',
    top: 0,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  activityIndicator: {
    width: 44,
    height: 44,
    justifyContent: 'center',
  },
})
